import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";

const NAV_LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "The Kumbh" },
  { to: "/packages", label: "Packages" },
  { to: "/plan-your-visit", label: "Plan Your Visit" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  return (
    <header
      data-testid="navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${
        scrolled ? "glass border-b hairline" : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-[1400px] items-center justify-between px-6 py-5 md:px-12">
        <Link to="/" data-testid="logo-link" className="group flex flex-col leading-none">
          <span className="overline text-primary">Simhastha</span>
          <span className="font-serif text-xl tracking-tight md:text-2xl">
            Ujjain <span className="italic text-primary">2028</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-10 md:flex" aria-label="Primary">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `overline transition-colors duration-300 hover:text-primary ${
                  isActive ? "text-primary" : "text-foreground/70"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:block">
          <Link to="/plan-your-visit">
            <Button
              data-testid="cta-curate-nav"
              className="rounded-none border border-primary bg-transparent px-6 py-6 text-primary hover:bg-primary hover:text-primary-foreground"
            >
              <span className="overline">Curate Your Journey</span>
            </Button>
          </Link>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className="text-foreground md:hidden"
          aria-label="Toggle menu"
        >
          {open ? <X strokeWidth={1.5} /> : <Menu strokeWidth={1.5} />}
        </button>
      </div>

      {open && (
        <div className="glass border-t hairline md:hidden" data-testid="mobile-menu">
          <div className="flex flex-col gap-6 px-6 py-8">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className="overline text-foreground/80"
              >
                {l.label}
              </NavLink>
            ))}
            <Link to="/plan-your-visit">
              <Button
                data-testid="cta-curate-nav-mobile"
                className="w-full rounded-none border border-primary bg-transparent px-6 py-6 text-primary hover:bg-primary hover:text-primary-foreground"
              >
                <span className="overline">Curate Your Journey</span>
              </Button>
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
